import type { ReportSpec } from '@clockwise/semantic-core';

/**
 * Handing a spec to `/reports/new` — "Open as its own report" from a drill.
 *
 * NOT IN THE URL. A landing spec carries the whole coordinate and the source's
 * filters, which is kilobytes of ids; a URL that size breaks in the places
 * links travel through. So the spec is parked here under a one-time nonce and
 * the URL carries only the nonce. Read once, then forgotten: a reload of the
 * builder afterwards is a blank builder, which is what `/reports/new` is.
 *
 * Browser storage can be missing or refuse (a private window, blocked site
 * data), so every access is guarded and a failure is simply no handoff.
 */
const PREFIX = 'clockwise.report-handoff.';

export function parkSpec(spec: ReportSpec): string | null {
  const nonce = Math.random().toString(36).slice(2, 10);
  try {
    localStorage.setItem(PREFIX + nonce, JSON.stringify(spec));
    return nonce;
  } catch {
    return null;
  }
}

/**
 * What each nonce held, once taken. Read-once storage and React's dev-mode
 * double render do not mix — the second read would find nothing — so the first
 * read is remembered for the life of the page.
 */
const taken = new Map<string, unknown>();

export function takeSpec(nonce: string | undefined): unknown {
  if (!nonce) return undefined;
  if (taken.has(nonce)) return taken.get(nonce);
  let value: unknown;
  try {
    const raw = localStorage.getItem(PREFIX + nonce);
    localStorage.removeItem(PREFIX + nonce);
    value = raw ? JSON.parse(raw) : undefined;
  } catch {
    value = undefined;
  }
  taken.set(nonce, value);
  return value;
}
